import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XClose } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import { Pager } from "@/components/app/Pager";
import { useConnectors } from "@/lib/connectors";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useTransactionLogs,
  useTransactionLogStats,
  useTransactionLog,
} from "@/lib/transactionLogs";
import { formatDateTime } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";

const inputCls =
  "rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";
const REFRESH_OPTIONS = [0, 5000, 10000, 30000];

/** `datetime-local` hodnota (`YYYY-MM-DDTHH:mm`) pro daný okamžik. */
function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Backend binduje fromDate/toDate jako `@DateTimeFormat(ISO.DATE_TIME) LocalDateTime`
 * (očekává `yyyy-MM-ddTHH:mm:ss`). `datetime-local` vrací jen minutovou přesnost,
 * `date` jen den → doplníme sekundy/čas, jinak Spring vrátí 400 typeMismatch.
 */
function toIsoDateTime(v: string): string {
  if (!v) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:00`;
  return v;
}

/**
 * Transakční logy (approve/reject audit) — stat karty, filtry, stránkovaná UUI Table
 * a detail (podpis / WYSIWYS / hashe / dešifrovaná data). Admin API.
 */
export function TransactionLogsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const [tokenIdInput, setTokenIdInput] = useState("");
  const [operationType, setOperationType] = useState("");
  const [success, setSuccess] = useState("");
  const [authServerType, setAuthServerType] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return isoLocal(d);
  });
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [refreshMs, setRefreshMs] = useState(0);
  const tokenId = useDebounce(tokenIdInput, 300);

  const connectors = useConnectors();
  const stats = useTransactionLogStats(refreshMs);
  const filter = useMemo(
    () => ({ tokenId, operationType, success, authServerType, fromDate: toIsoDateTime(fromDate), toDate: toIsoDateTime(toDate) }),
    [tokenId, operationType, success, authServerType, fromDate, toDate],
  );
  const { data, isLoading, isError, isFetching } = useTransactionLogs(page, pageSize, filter, refreshMs);

  const [detailId, setDetailId] = useState<number | null>(null);

  const hasFilters = tokenIdInput || operationType || success || authServerType || fromDate || toDate;
  const clear = () => { setTokenIdInput(""); setOperationType(""); setSuccess(""); setAuthServerType(""); setFromDate(""); setToDate(""); setPage(0); };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("txlogs.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("txlogs.subtitle")}</p>
      </div>

      {/* Stat karty */}
      {stats.data && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label={t("txlogs.stat.total")} value={stats.data.totalOperations} />
          <StatCard label={t("txlogs.stat.success")} value={stats.data.successfulOperations} />
          <StatCard label={t("txlogs.stat.failed")} value={stats.data.failedOperations} />
          <StatCard label={t("txlogs.stat.successRate")} value={`${stats.data.successRate.toFixed(1)} %`} />
          <StatCard label={t("txlogs.stat.approves")} value={stats.data.approveOperations} />
          <StatCard label={t("txlogs.stat.rejects")} value={stats.data.rejectOperations} />
          <StatCard label={t("txlogs.stat.last24h")} value={stats.data.operationsLast24h} />
          <StatCard label={t("txlogs.stat.avgDuration")} value={`${Math.round(stats.data.averageDurationMs)} ms`} />
        </div>
      )}

      {/* Filtry */}
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label={t("tokens.col.tokenId")}>
          <input className={inputCls} value={tokenIdInput} onChange={(e) => { setTokenIdInput(e.target.value); setPage(0); }} />
        </FilterField>
        <FilterField label={t("txlogs.col.operation")}>
          <AppSelect fullWidth={false} value={operationType} onChange={(e) => { setOperationType(e.target.value); setPage(0); }}>
            <option value="">{t("tokens.filter.all")}</option>
            <option value="APPROVE">APPROVE</option>
            <option value="REJECT">REJECT</option>
          </AppSelect>
        </FilterField>
        <FilterField label={t("txlogs.col.result")}>
          <AppSelect fullWidth={false} value={success} onChange={(e) => { setSuccess(e.target.value); setPage(0); }}>
            <option value="">{t("tokens.filter.all")}</option>
            <option value="true">{t("txlogs.ok")}</option>
            <option value="false">{t("txlogs.fail")}</option>
          </AppSelect>
        </FilterField>
        <FilterField label={t("tokens.col.connector")}>
          <AppSelect fullWidth={false} value={authServerType} onChange={(e) => { setAuthServerType(e.target.value); setPage(0); }}>
            <option value="">{t("tokens.filter.all")}</option>
            {connectors.data?.map((c) => <option key={c.connectorType} value={c.connectorType}>{c.connectorType}</option>)}
          </AppSelect>
        </FilterField>
        <FilterField label={t("txlogs.filter.from")}>
          <input type="datetime-local" className={inputCls} value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} />
        </FilterField>
        <FilterField label={t("txlogs.filter.to")}>
          <input type="datetime-local" className={inputCls} value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} />
        </FilterField>
        <FilterField label={t("pager.pageSize")}>
          <AppSelect fullWidth={false} value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}>
            {[20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
          </AppSelect>
        </FilterField>
        <FilterField label={t("txlogs.filter.autoRefresh")}>
          <AppSelect fullWidth={false} value={refreshMs} onChange={(e) => setRefreshMs(parseInt(e.target.value, 10))}>
            {REFRESH_OPTIONS.map((ms) => (
              <option key={ms} value={ms}>{ms === 0 ? t("txlogs.filter.off") : `${ms / 1000}s`}</option>
            ))}
          </AppSelect>
        </FilterField>
        {refreshMs > 0 && <span className="pb-2 text-xs text-success-primary">● {t("txlogs.filter.live")}</span>}
        {hasFilters && (
          <Button color="secondary" size="md" iconLeading={XClose} onPress={clear}>{t("tokens.filter.clear")}</Button>
        )}
      </div>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <>
          <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
            <Table aria-label={t("txlogs.title")} selectionMode="none">
              <Table.Header>
                <Table.Head isRowHeader id="createdAt" label={t("txlogs.col.created")} />
                <Table.Head id="operationType" label={t("txlogs.col.operation")} />
                <Table.Head id="success" label={t("txlogs.col.result")} />
                <Table.Head id="tokenId" label={t("tokens.col.tokenId")} />
                <Table.Head id="transactionId" label={t("txlogs.col.txId")} />
                <Table.Head id="username" label={t("tokens.col.username")} />
                <Table.Head id="authServerType" label={t("tokens.col.connector")} />
                <Table.Head id="durationMs" label={t("txlogs.col.duration")} />
              </Table.Header>
              <Table.Body items={data.content}>
                {(row) => (
                  <Table.Row id={String(row.id)} onAction={() => setDetailId(row.id)} className="cursor-pointer">
                    <Table.Cell>{formatDateTime(row.createdAt, { seconds: true })}</Table.Cell>
                    <Table.Cell>
                      <Badge type="pill-color" size="sm" color={row.operationType === "APPROVE" ? "blue" : "orange"}>
                        {row.operationType}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge type="pill-color" size="sm" color={row.success ? "success" : "error"}>
                        {row.success ? t("txlogs.ok") : t("txlogs.fail")}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="font-medium text-primary">{row.tokenId}</Table.Cell>
                    <Table.Cell className="font-mono text-xs">{row.transactionId ?? "—"}</Table.Cell>
                    <Table.Cell>{row.username ?? "—"}</Table.Cell>
                    <Table.Cell>{row.authServerType}</Table.Cell>
                    <Table.Cell>{row.durationMs != null ? `${row.durationMs} ms` : "—"}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <Pager page={data} onPage={setPage} isFetching={isFetching} />
        </>
      )}

      {detailId != null && <DetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg bg-secondary p-3 ring-1 ring-secondary">
      <div className="text-xl font-semibold text-primary">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="text-xs text-tertiary">{label}</div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-tertiary">{label}</span>
      {children}
    </label>
  );
}

function DetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useTransactionLog(id);
  return (
    <AppDialog isOpen onClose={onClose} size="3xl" title={t("txlogs.detailTitle", { id })}>
      {isLoading && <LoadingIndicator />}
      {data && (
        <div className="flex flex-col gap-2 text-sm">
          <Row label={t("txlogs.col.operation")}>{data.operationType}</Row>
          <Row label={t("txlogs.col.result")}>{data.success ? t("txlogs.ok") : `${t("txlogs.fail")}${data.errorMessage ? ` — ${data.errorMessage}` : ""}`}</Row>
          <Row label={t("tokens.col.tokenId")}>{data.tokenId}</Row>
          <Row label={t("tokens.col.username")}>{data.username ?? "—"}</Row>
          <Row label={t("tokens.col.connector")}>{data.authServerType}</Row>
          <Row label={t("txlogs.field.transactionId")}>{data.transactionId ?? "—"}</Row>
          <Row label={t("txlogs.field.authServerTxId")}>{data.authServerTransactionId ?? "—"}</Row>
          <Row label={t("txlogs.field.paatProfile")}>{data.paatProfileId ?? "—"}</Row>
          <Row label={t("txlogs.field.deviceAuthType")}>{data.deviceAuthType ?? "—"}</Row>
          <Row label={t("txlogs.col.duration")}>{data.durationMs != null ? `${data.durationMs} ms` : "—"}</Row>
          <Row label={t("txlogs.field.createdAt")}>{formatDateTime(data.createdAt, { seconds: true })}</Row>
          <Row label={t("txlogs.field.startedAt")}>{formatDateTime(data.operationStartedAt, { seconds: true })}</Row>
          <Row label={t("txlogs.field.completedAt")}>{formatDateTime(data.operationCompletedAt, { seconds: true })}</Row>
          <Row label={t("txlogs.field.traceId")}>{data.traceId ?? "—"}</Row>
          <Row label={t("txlogs.field.correlationId")}>{data.requestCorrelationId ?? "—"}</Row>
          <Row label={t("txlogs.field.requestIp")}>{data.requestIp ?? "—"}</Row>
          <Row label={t("txlogs.field.userAgent")}>{data.userAgent ?? "—"}</Row>
          <Row label={t("txlogs.field.errorCode")}>{data.errorCode ?? "—"}</Row>
          <Row label={t("txlogs.field.signature")}>{data.signature ? (data.signatureSource ?? "—") : "—"}</Row>
          {data.signature && <Block label={t("txlogs.field.signatureValue")} text={data.signature} />}
          <Row label={t("txlogs.field.encryptedHash")}>{data.encryptedDataHash ?? "—"}</Row>
          <Row label={t("txlogs.field.scmsHash")}>{data.secureChannelMessageSignatureHash ?? "—"}</Row>
          <Row label={t("txlogs.field.password")}>{data.passwordProvided ? `✓ ${data.passwordSource ?? ""}` : "—"}</Row>
          <Row label={t("txlogs.field.rejectionReason")}>{data.rejectionReason ?? "—"}</Row>
          {data.mobileSdkData && <Block label={t("txlogs.field.mobileSdkData")} text={JSON.stringify(data.mobileSdkData, null, 2)} />}
          {data.wysiwygData && <Block label={t("txlogs.field.wysiwyg")} text={data.wysiwygData} />}
          {data.decryptedData && <Block label={t("txlogs.field.decrypted")} text={JSON.stringify(data.decryptedData, null, 2)} />}
        </div>
      )}
    </AppDialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 border-b border-secondary py-1.5">
      <span className="text-tertiary">{label}</span>
      <span className="break-all text-primary">{children}</span>
    </div>
  );
}

function Block({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-tertiary">{label}</div>
      <pre className="max-h-[40vh] overflow-auto rounded-md bg-secondary p-2 font-mono text-xs text-secondary ring-1 ring-secondary">{text}</pre>
    </div>
  );
}
